/**
 * This phone's own "this stage is running late" reports (the user,
 * 2026-10-08). Kept on the phone (`myrave:{editionId}:delays`) so they apply
 * at once and with no signal — every screen reads the shifted times through
 * `EditionProvider` — and sent to the API when there is a network and a
 * session (`POST /editions/:id/delays`), where ten matching reports shift the
 * stage for everybody.
 */
import { api, hasSession } from './api';
import { readJson, writeJson } from './storage';

export interface MyDelay { stageId: string; fromActId: string; minutes: number; synced: boolean }

const KEY = 'delays';
const listeners = new Map<string, Set<() => void>>();
const cache = new Map<string, { raw: string; list: MyDelay[] }>();

function changed(editionId: string): void {
  for (const cb of listeners.get(editionId) ?? []) cb();
}

export function subscribeMyDelays(editionId: string, cb: () => void): () => void {
  const set = listeners.get(editionId) ?? new Set();
  set.add(cb);
  listeners.set(editionId, set);
  return () => { set.delete(cb); };
}

/** The reports, as a stable array per stored value (a `useSyncExternalStore` snapshot). */
export function readMyDelays(editionId: string): MyDelay[] {
  const list = readJson<MyDelay[]>(editionId, KEY, []);
  const raw = JSON.stringify(list);
  const hit = cache.get(editionId);
  if (hit && hit.raw === raw) return hit.list;
  cache.set(editionId, { raw, list });
  return list;
}

/** Say this stage runs `minutes` late from `fromActId` — replacing this phone's report for that set. */
export function reportDelay(editionId: string, stageId: string, fromActId: string, minutes: number): void {
  const rest = readMyDelays(editionId).filter((d) => d.fromActId !== fromActId);
  writeJson(editionId, KEY, [...rest, { stageId, fromActId, minutes, synced: false }]);
  changed(editionId);
  void flushMyDelays(editionId);
}

/** Take this phone's report back — here at once, at the API when it can be reached. */
export function withdrawDelay(editionId: string, fromActId: string): void {
  writeJson(editionId, KEY, readMyDelays(editionId).filter((d) => d.fromActId !== fromActId));
  changed(editionId);
  if (hasSession()) void api.del(`/editions/${editionId}/delays/${encodeURIComponent(fromActId)}`).catch(() => undefined);
}

let flushing = false;
/** Send the reports the API has not had yet. Never throws; a failure waits for the next try. */
export async function flushMyDelays(editionId: string): Promise<void> {
  if (flushing || !hasSession()) return;
  flushing = true;
  try {
    for (const d of readMyDelays(editionId).filter((x) => !x.synced)) {
      try {
        await api.post(`/editions/${editionId}/delays`, { actId: d.fromActId, minutes: d.minutes });
        writeJson(editionId, KEY, readMyDelays(editionId).map((x) => (x.fromActId === d.fromActId && x.minutes === d.minutes ? { ...x, synced: true } : x)));
        changed(editionId);
      } catch {
        return; // offline, or refused: tried again on the next change, load or `online`
      }
    }
  } finally {
    flushing = false;
  }
}
